import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { Download, HelpCircle, Search } from 'lucide-react'
import './Members.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom } from '../../AtomStore'
import { drumtaleAdminTokenAtom } from '../../AtomStore'
import { gradeAccessPolicy, openPageSpecification } from '../shared/pageSpecification'
import {
  Member,
  memberGradeOptions,
  memberJoinTypeOptions,
  memberStatusOptions,
  membersDataModelGuide,
  membersImplementationGuide,
  membersSpecification,
  membersStateDefinitions,
} from './membersData'

const TOAST_DURATION_MS = 2400

type PeriodMode = 'day' | 'week' | 'month'

function matchesPeriod(joined: string, start: string, end: string) {
  if (!joined || joined === '-') return false
  const dateText = joined.slice(0, 10)
  return dateText >= (start || '1900-01-01') && dateText <= (end || '2999-12-31')
}

function matchesSearch(query: string, member: Member) {
  if (!query) return true
  const haystack = [member.name, member.phone, member.kakao, member.grade, member.joinType].join(' ').toLowerCase()
  return haystack.includes(query.toLowerCase())
}

function gradeBadgeType(grade: string) {
  return grade === '일반' ? '' : 'success'
}

function statusBadgeType(status: string) {
  if (status === '구독 중') return 'success'
  if (status === '미구독') return ''
  return 'warning'
}

function accountBadgeType(account: string) {
  return account === '정상' ? 'success' : 'danger'
}

function Members() {
  const navigate = useNavigate()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)
  const adminToken = useAtomValue(drumtaleAdminTokenAtom)

  const [search, setSearch] = useState('')
  const [grade, setGrade] = useState('')
  const [joinType, setJoinType] = useState('')
  const [status, setStatus] = useState('')
  const [periodMode, setPeriodMode] = useState<PeriodMode>('month')
  const [periodStart, setPeriodStart] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  })
  const [periodEnd, setPeriodEnd] = useState(() => {
    const now = new Date()
    return now.toISOString().slice(0, 10)
  })
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set())

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  const [membersFromServer, setMembersFromServer] = useState<Member[]>([])
  const [isLoadingMembers, setIsLoadingMembers] = useState(false)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    if (!isAdminSessionActive || !adminToken) return

    const loadMembers = async () => {
      setIsLoadingMembers(true)
      try {
        const MainURL = (await import('../../MainURL')).default
        const response = await fetch(`${MainURL}/admin/members`, {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        })

        const data = await response.json().catch(() => null)
        console.log('[Members] response status:', response.status, 'data:', data)
        if (!response.ok) {
          showToast(data?.message || '회원 목록을 불러오지 못했습니다.')
          return
        }

        setMembersFromServer(data?.members ?? [])
      } catch (error) {
        console.error('[Members] fetch error:', error)
        showToast('회원 목록을 불러오지 못했습니다.')
      } finally {
        setIsLoadingMembers(false)
      }
    }

    loadMembers()
  }, [adminToken, isAdminSessionActive])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const filteredMembers = useMemo(() => {
    return membersFromServer
      .filter((member) => matchesPeriod(member.joined, periodStart, periodEnd))
      .filter((member) => !grade || member.grade === grade)
      .filter((member) => !joinType || member.joinType === joinType)
      .filter((member) => !status || member.status === status)
      .filter((member) => matchesSearch(search, member))
  }, [search, grade, joinType, status, periodStart, periodEnd, membersFromServer])

  const isAllSelected = filteredMembers.length > 0 && filteredMembers.every((member) => selectedNames.has(member.name))

  const toggleSelectAll = () => {
    setSelectedNames((previous) => {
      if (isAllSelected) {
        const next = new Set(previous)
        filteredMembers.forEach((member) => next.delete(member.name))
        return next
      }
      const next = new Set(previous)
      filteredMembers.forEach((member) => next.add(member.name))
      return next
    })
  }

  const toggleSelectRow = (name: string) => {
    setSelectedNames((previous) => {
      const next = new Set(previous)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })
  }

  const handlePeriodModeChange = (mode: PeriodMode) => {
    setPeriodMode(mode)
    const today = new Date()
    const todayStr = today.toISOString().slice(0, 10)
    if (mode === 'week') {
      const weekAgo = new Date(today)
      weekAgo.setDate(today.getDate() - 6)
      setPeriodStart(weekAgo.toISOString().slice(0, 10))
      setPeriodEnd(todayStr)
    } else if (mode === 'day') {
      setPeriodStart(todayStr)
      setPeriodEnd(todayStr)
    } else {
      setPeriodStart(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`)
      setPeriodEnd(todayStr)
    }
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(membersSpecification, membersDataModelGuide, membersImplementationGuide, {
      gradeAccessPolicy,
      stateDefinitions: membersStateDefinitions,
    })
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  return (
    <div className='Members'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <h1>회원관리</h1>
            </div>
          </header>

          <section className='view active'>
            <div className='toolbar'>
              <div className='filter-group'>
                <label className='inline-search'>
                  <Search size={15} />
                  <input
                    type='search'
                    placeholder='회원명 또는 Google 이메일'
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                <div className='period-range'>
                  <select
                    className='filter period-filter'
                    value={periodMode}
                    onChange={(event) => handlePeriodModeChange(event.target.value as PeriodMode)}
                  >
                    <option value='day'>일간</option>
                    <option value='week'>주간</option>
                    <option value='month'>월간</option>
                  </select>
                  <input
                    className='date-filter'
                    type='date'
                    value={periodStart}
                    onChange={(event) => setPeriodStart(event.target.value)}
                  />
                  <span>~</span>
                  <input
                    className='date-filter'
                    type='date'
                    value={periodEnd}
                    onChange={(event) => setPeriodEnd(event.target.value)}
                  />
                </div>
                <select className='filter' value={grade} onChange={(event) => setGrade(event.target.value)}>
                  <option value=''>회원 등급 전체</option>
                  {memberGradeOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <select className='filter' value={joinType} onChange={(event) => setJoinType(event.target.value)}>
                  <option value=''>OS 유형 전체</option>
                  {memberJoinTypeOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <select className='filter' value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value=''>상태 전체</option>
                  {memberStatusOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <button
                className='secondary-button'
                type='button'
                onClick={() => showToast('회원 목록 엑셀 다운로드를 준비했습니다.')}
              >
                <Download size={15} />
                엑셀다운로드
              </button>
            </div>

            <div className='table-wrap'>
              <table>
                <thead>
                  <tr>
                    <th><input type='checkbox' checked={isAllSelected} onChange={toggleSelectAll} /></th>
                    <th>회원</th>
                    <th>Google 이메일</th>
                    <th>전화번호</th>
                    <th>가입날짜</th>
                    <th>구독정보</th>
                    <th>구독</th>
                    <th>이번 달 드럼</th>
                    <th>이번 달 인지</th>
                    <th>최근 리포트</th>
                    <th>계정 상태</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingMembers ? (
                    <tr><td colSpan={12}>불러오는 중...</td></tr>
                  ) : filteredMembers.length === 0 ? (
                    <tr><td colSpan={12}>검색 조건에 맞는 데이터가 없습니다.</td></tr>
                  ) : (
                    filteredMembers.map((member) => (
                      <tr key={member.name}>
                        <td>
                          <input
                            type='checkbox'
                            checked={selectedNames.has(member.name)}
                            onChange={() => toggleSelectRow(member.name)}
                          />
                        </td>
                        <td>
                          <div className='person'>
                            <span className='avatar'>{member.name.slice(0, 1)}</span>
                            <div>
                              <strong>{member.name}</strong>
                            </div>
                          </div>
                        </td>
                        <td>{member.kakao}</td>
                        <td>{member.phone}</td>
                        <td>{member.joined}</td>
                        <td><span className={`badge ${gradeBadgeType(member.grade)}`}>{member.grade}</span></td>
                        <td><span className={`badge ${statusBadgeType(member.status)}`}>{member.status}</span></td>
                        <td>{member.drum.toLocaleString()}회</td>
                        <td>{member.cognitive.toLocaleString()}회</td>
                        <td>{member.report}</td>
                        <td><span className={`badge ${accountBadgeType(member.account)}`}>{member.account}</span></td>
                        <td className='row-actions'>
                          <button
                            className='secondary-button'
                            type='button'
                            onClick={() =>
                              navigate(`/admin/members/${encodeURIComponent(member.name)}`, { state: { member } })
                            }
                          >
                            상세
                          </button>
                          <button
                            className='secondary-button danger'
                            type='button'
                            onClick={() => showToast('회원 계정 제어 기능은 추후 제공됩니다.')}
                          >
                            정지
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default Members
